import { z } from 'zod';

import type { WellnessInsight, WellnessInsightMetric } from './types';

const wellnessInsightResponseSchema = z
  .object({
    insights: z.array(
      z
        .object({
          metricId: z.string(),
          advice: z.string().trim().min(1).max(100),
        })
        .strict(),
    ),
  })
  .strict();

// A partial response is a failed attempt, so the caller can retry instead of
// silently leaving a selected metric without a recommendation.
export const parseWellnessInsightAssistantResponse = (
  selectedMetrics: readonly WellnessInsightMetric[],
  assistantResponse: string,
): WellnessInsight[] | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(assistantResponse);
  } catch {
    return null;
  }

  const result = wellnessInsightResponseSchema.safeParse(parsed);
  if (!result.success || result.data.insights.length !== selectedMetrics.length) {
    return null;
  }

  const insightsByMetric = new Map<string, WellnessInsight>();

  for (const insight of result.data.insights) {
    const selectedMetric = selectedMetrics.find(({ metricId }) => metricId === insight.metricId);
    if (!selectedMetric || insightsByMetric.has(insight.metricId)) {
      return null;
    }

    insightsByMetric.set(insight.metricId, { ...insight, metricId: selectedMetric.metricId });
  }

  return selectedMetrics.flatMap(({ metricId }) => {
    const insight = insightsByMetric.get(metricId);
    return insight ? [insight] : [];
  });
};
